import BellSvg from 'components/_common/Icons/SVGIcons/BellSVG';
import { notify } from 'helpers/NotifyHelper';
import { t } from 'i18next';
import React, { useState, useRef, useEffect } from 'react';
import { markAllNotificationsRead } from 'store/admin/actions/AdminNotificationAction';
import {
  notificationsSelector,
  loadingNotificationsSelector,
  loadingMoreNotificationsSelector,
} from 'store/admin/selectors/AdminGlobalSelector';
import {
  allNotificationsMarkedRead,
  fetchMoreNotifications,
} from 'store/admin/slices/AdminGlobalSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import NotificationCard from './NotificationCard';

interface NotificationDropdownProps {
  loading?: boolean;
  // called each time the panel opens
  onOpen?: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ loading, onOpen }) => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const notifications = useAppSelector(notificationsSelector);
  const isLoading = useAppSelector(loadingNotificationsSelector) || loading;
  const loadingMore = useAppSelector(loadingMoreNotificationsSelector);

  const toggleDropdown = () => {
    if (!open) onOpen?.();
    setOpen(!open);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // The list is unread-only, so total_records is the unread count
  const unreadCount = notifications?.total_records ?? 0;
  const notificationData = notifications?.data ?? [];
  const hasMore = notificationData.length < unreadCount;

  const handleMarkAllRead = async () => {
    if (markingAll) return;
    try {
      setMarkingAll(true);
      const response: any = await markAllNotificationsRead();
      if (!response?.status) {
        notify.error(response?.message ?? t('something_went_wrong'));
        return;
      }
      dispatch(allNotificationsMarkedRead());
    } catch (error: any) {
      notify.error(error.message ?? t('something_went_wrong'));
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div className="notification-wrapper" ref={ref}>
      <div className="notification-icon" onClick={toggleDropdown}>
        <BellSvg />
        <span className="badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
      </div>
      {open && (
        <div className="notification-panel">
          <div className="notification-header">
            <div className="notification-title">
              <BellSvg width={24} height={24} stroke="#9B9DAF" />
              <span>{t('notifications')}</span>
            </div>
            {unreadCount > 0 && !isLoading && (
              <button
                type="button"
                className="notification-mark-all"
                onClick={handleMarkAllRead}
                disabled={markingAll}
              >
                {t('mark_all_as_read')}
              </button>
            )}
          </div>

          <div className="notification-body">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="notification-card skeleton" />
              ))
            ) : (
              <>
                {notificationData.length > 0 ? (
                  notificationData.map((item: any) => (
                    <NotificationCard item={item} key={item.id} />
                  ))
                ) : !hasMore ? (
                  <div className="no-notifications">{t('no_unread_notifications')}</div>
                ) : null}

                {hasMore && (
                  <button
                    type="button"
                    className="notification-load-more"
                    onClick={() => dispatch(fetchMoreNotifications())}
                    disabled={loadingMore}
                  >
                    {loadingMore ? `${t('loading')}...` : t('load_more')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
